import { SnackbarCloseReason } from '@mui/material';
import Notification from 'components/ui/Notification';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

type ContextType = {
  notify: {
    [notifyType in NotificationType]: (msg: string | React.ReactNode) => void;
  };
};
export const NotificationContext = createContext<ContextType | undefined>(
  undefined,
);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [message, setMessage] = useState<string | React.ReactNode>('');
  const [open, setOpen] = useState<boolean>(false);
  const [type, setType] = useState<NotificationType>('success');
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleNotify = useCallback(
    (msg: string | React.ReactNode, type: NotificationType) => {
      setOpen(true);
      setMessage(msg);
      setType(type);
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }
      closeTimeout.current = setTimeout(() => {
        setOpen(false);
      }, 3000);
    },
    [],
  );
  const notify = useMemo(() => {
    return {
      success: (msg: string | React.ReactNode) => {
        handleNotify(msg, 'success');
      },
      error: (msg: string | React.ReactNode) => {
        handleNotify(msg, 'error');
      },
      warning: (msg: string | React.ReactNode) => {
        handleNotify(msg, 'warning');
      },
      info: (msg: string | React.ReactNode) => {
        handleNotify(msg, 'info');
      },
    };
  }, [handleNotify]);
  const handleClose = useCallback(
    (event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
      if (reason === 'clickaway') {
        return;
      }
      setOpen(false);
    },
    [],
  );
  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <Notification
        open={open}
        onClose={handleClose}
        message={message}
        type={type}
      />
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotification must be used within a NotificationProvider',
    );
  }
  return context.notify;
};
